import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useConfirm from 'src/hooks/useConfirm';

import useBoolean from 'src/hooks/useBoolean';
import { useCancelInvoicesMutation } from 'src/services/invoices';
import useCancelReceiptsApi from 'src/services/useCancelReceiptsApi';
import useLinkInvoice from 'src/services/useLinkInvoice';
import { PoDocumentObject } from 'src/services/usePoDocumentsApi';
import { SetState } from 'src/types/generic';
import { isEqualString } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';
import { CancelSelectedDocumentInternalContent } from '../invoice_details/ModalInvoiceDialogs';
import { PoRelatedDocumentsContext } from './PoRelatedDocumentsContext';

const usePoRelatedDocumentsActions = (
  poId: string,
  getDetails: () => void,
  isDirty: boolean,
  refresh: VoidFunction,
  isInvoice: boolean,
  isEditDisabled: boolean,
  setIsNewlyLinked?: SetState<boolean>,
  setCreateNewDocModel?: SetState<boolean>
) => {
  const poDocuments = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDocuments);
  const setPoDocuments = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value.setPoDocuments
  );
  const getPoDocuments = useContextSelector(PoRelatedDocumentsContext, (value) => value.refresh);
  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const { confirm } = useConfirm();
  const { cancelReceipts, cancelReceiptsLoading } = useCancelReceiptsApi();
  const { mutateAsync: cancelInvoices, isPending: cancelInvoicesLoading } =
    useCancelInvoicesMutation();
  const [searchParams] = useSearchParams();
  const receiptId = useMemo(() => searchParams.get('recid'), [searchParams]);
  const { value: isCreatingInvoiceOrReceipt, toggle: toggleIsCreatingInvoiceOrReceipt } =
    useBoolean();
  const [documentType, setDocumentType] = useState<boolean>(false);

  const {
    isCancelDocumentsDisabled,
    isLinkDisabled,
    isCurrentDocumentReceipt,
    currentDocument,
    selectedDocuments,
    cancelDocumentsTooltipContent,
    linkTooltipContent,
  } = useMemo(() => {
    const selectedDocuments = poDocuments?.filter((doc) => doc.selected) || [];

    const isEveryDocumentInProgress =
      selectedDocuments?.every?.((document) =>
        document?.status?.toLowerCase()?.includes('in progress')
      ) || false;
    const notOneSelectedDocument = selectedDocuments?.length !== 1;
    const currentDocument = poDocuments?.find((doc) => doc.documentId.toString() === receiptId);
    const isCurrentDocumentAlreadyLinked = !!currentDocument?.linkedDocumentId;
    const isCurrentDocumentReceipt = currentDocument?.documentType === 'Receipt';
    const isSelectedDocumentLinked = selectedDocuments[0]?.linkedDocumentId !== null;
    const isCurrentAndSelectedDocumentTypeSame =
      currentDocument?.documentType === selectedDocuments?.[0]?.documentType;
    const hasCorrectStatus = isCurrentDocumentReceipt
      ? isEqualString(currentDocument?.status, 'complete') &&
        isEqualString(selectedDocuments[0]?.status, 'in progress')
      : isEqualString(currentDocument?.status, 'in progress') &&
        isEqualString(selectedDocuments[0]?.status, 'complete');

    let linkTooltipContent: string = '';
    let cancelDocumentsTooltipContent: string = '';
    const isCancelDocumentsDisabled: boolean =
      !selectedDocuments.length || !selectedDocuments.every((doc) => doc.canCancel);

    if (isEmpty(selectedDocuments)) {
      cancelDocumentsTooltipContent = 'Please select at least one document';
    } else if (!isEveryDocumentInProgress) {
      cancelDocumentsTooltipContent = 'Some of the selected documents are not in progress';
    } else if (isCancelDocumentsDisabled) {
      cancelDocumentsTooltipContent = 'Selected Documents are read only';
    }
    if (isCurrentDocumentAlreadyLinked) {
      linkTooltipContent = 'Current document is already linked';
    } else if (notOneSelectedDocument) {
      linkTooltipContent = 'Please select one document to link';
    } else if (isDirty) {
      linkTooltipContent = `Please save the ${isInvoice ? 'invoice' : 'receipt'}`;
    } else if (isCurrentAndSelectedDocumentTypeSame) {
      linkTooltipContent = isCurrentDocumentReceipt
        ? 'Only an Invoice/Credit Memo can be linked to a Receipt'
        : `Only a Receipt can be linked to ${title === 'Invoice' ? 'an Invoice' : 'a Credit Memo'}`;
    } else if (!hasCorrectStatus) {
      linkTooltipContent =
        'Only a Completed Receipt and In Progress Invoice/Credit Memo can be linked';
    } else if (isSelectedDocumentLinked) {
      linkTooltipContent = 'Selected document is already linked';
    }

    const isLinkDisabled =
      notOneSelectedDocument ||
      isCurrentDocumentAlreadyLinked ||
      !hasCorrectStatus ||
      isCurrentAndSelectedDocumentTypeSame ||
      isSelectedDocumentLinked ||
      isDirty ||
      isEditDisabled;

    return {
      selectedDocuments,
      isLinkDisabled,
      isCancelDocumentsDisabled,
      currentDocument,
      isCurrentDocumentReceipt,
      cancelDocumentsTooltipContent,
      linkTooltipContent,
    };
  }, [isDirty, isEditDisabled, isInvoice, poDocuments, receiptId, title]);

  const receiptToLink: string = String(
    isCurrentDocumentReceipt
      ? currentDocument?.documentId.toString()
      : selectedDocuments?.[0]?.documentId.toString()
  );
  const invoiceToLink: string = String(
    isCurrentDocumentReceipt
      ? selectedDocuments?.[0]?.documentId.toString()
      : currentDocument?.documentId.toString()
  );

  const { linkInvoice, isLoading: isLinkDocumentButtonLoading } = useLinkInvoice(
    invoiceToLink,
    receiptToLink
  );

  const isCancelDocumentButtonLoading = useMemo(
    () => cancelReceiptsLoading || cancelInvoicesLoading,
    [cancelInvoicesLoading, cancelReceiptsLoading]
  );
  const cancelSelectedDocuments = useCallback(() => {
    const linkedDocument =
      selectedDocuments.find((doc) => doc.linkedDocumentId) || ({} as PoDocumentObject);
    confirm({
      title: 'Verify Cancellation',
      content: (
        <CancelSelectedDocumentInternalContent
          linkedDocumentId={linkedDocument?.linkedDocumentId}
          documentType={linkedDocument.documentType}
          poId={poId}
        />
      ),
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      width: '500px',
      confirmButtonText: 'OK',
    }).then(async (isCancelApproved) => {
      if (isCancelApproved) {
        const isCurrentDocument = selectedDocuments.some(
          (document) => String(document.documentId) === receiptId
        );
        const { invoiceIds, receiptIds } = selectedDocuments.reduce<{
          invoiceIds: number[];
          receiptIds: number[];
        }>(
          (acc, doc) => {
            const docType = doc.documentType?.toLowerCase();
            if (docType.includes('receipt')) {
              acc.receiptIds?.push(doc?.documentId);
            } else {
              acc.invoiceIds?.push(doc?.documentId);
            }

            return acc;
          },

          { invoiceIds: [], receiptIds: [] }
        );
        await Promise.all([
          receiptIds.length && cancelReceipts({ receiptIds }),
          invoiceIds.length && cancelInvoices(invoiceIds),
        ]);
        getPoDocuments();
        if (isCurrentDocument) getDetails();
      } else {
        setPoDocuments((prev) => {
          const prevItems = [...prev];
          return prevItems.map((item) => ({ ...item, selected: false }));
        });
      }
    });
  }, [
    cancelInvoices,
    cancelReceipts,
    confirm,
    getDetails,
    getPoDocuments,
    poId,
    receiptId,
    selectedDocuments,
    setPoDocuments,
  ]);

  const handleEDI = useCallback(async () => {
    await confirm({
      title: 'EDI 810 Enabled',
      content: 'There may be a pending transmission and a duplicate record may be created',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Continue',
      width: '500px',
    }).then((isConfirm) => {
      if (isConfirm) {
        toggleIsCreatingInvoiceOrReceipt();
      }
    });
  }, [confirm, toggleIsCreatingInvoiceOrReceipt]);

  const handleCreateInvoice = useCallback(() => {
    setCreateNewDocModel?.(false);
    setDocumentType(true);
    if (poDetails?.isEDIReceipt) {
      handleEDI();
    } else {
      toggleIsCreatingInvoiceOrReceipt();
    }
  }, [handleEDI, poDetails?.isEDIReceipt, setCreateNewDocModel, toggleIsCreatingInvoiceOrReceipt]);

  const handleCreateReceipt = useCallback(() => {
    setCreateNewDocModel?.(false);
    setDocumentType(false);
    toggleIsCreatingInvoiceOrReceipt();
  }, [setCreateNewDocModel, toggleIsCreatingInvoiceOrReceipt]);

  const linkToSelected = useCallback(async () => {
    try {
      await linkInvoice();
      setIsNewlyLinked?.(true);
      getDetails();
      refresh();
      toast.success('Linked to selected document successfully');
    } catch (error) {
      console.error(error);
      setIsNewlyLinked?.(false);
    }
  }, [getDetails, linkInvoice, refresh, setIsNewlyLinked]);

  return {
    toggleIsCreatingInvoiceOrReceipt,
    isCreatingInvoiceOrReceipt,
    handleCreateInvoice,
    handleCreateReceipt,
    cancelSelectedDocuments,
    isCancelDocumentsDisabled,
    isLinkDisabled,
    linkToSelected,
    cancelDocumentsTooltipContent,
    linkTooltipContent,
    getPoDocuments,
    documentType,
    isLinkDocumentButtonLoading,
    isCancelDocumentButtonLoading,
  } as const;
};

export default usePoRelatedDocumentsActions;
